/**
 * FileUpload con look Quantia:
 *  - Dropzone con borde dashed morado tenue, fondo lavanda muy suave.
 *  - Estado activo: borde sólido morado + fondo morado al 8%.
 *  - Lista de archivos: filas blancas con badge tipo y botón eliminar discreto.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Stack,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { COLORS } from '../theme/theme';
import { Badge } from './quantia';

const FileUpload = ({
  onFilesChange,
  acceptedTypes = '.pdf',
  multiple = true,
  label = 'Arrastra archivos PDF aquí o haz clic para seleccionar',
}) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState('');

  const validateFile = (file) => {
    const acceptedExtensions = acceptedTypes.split(',').map((ext) => ext.trim());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    return acceptedExtensions.includes(fileExtension);
  };

  const handleFiles = (newFiles) => {
    setError('');
    const validFiles = [];
    const invalidFiles = [];

    Array.from(newFiles).forEach((file) => {
      if (validateFile(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      setError(`Archivos no válidos: ${invalidFiles.join(', ')}`);
    }

    const updatedFiles = multiple ? [...files, ...validFiles] : validFiles;
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const handleFileInput = (event) => {
    if (event.target.files.length > 0) {
      handleFiles(event.target.files);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const inputId = `qa-file-input-${React.useId()}`;

  return (
    <Box>
      {/* Área de drop */}
      <Box
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById(inputId)?.click()}
        sx={{
          border: '2px dashed',
          borderColor: isDragging
            ? COLORS.primary
            : 'rgba(91, 79, 240, 0.35)',
          backgroundColor: isDragging
            ? 'rgba(91, 79, 240, 0.08)'
            : 'rgba(91, 79, 240, 0.04)',
          borderRadius: 4,
          p: 5,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: COLORS.primary,
            backgroundColor: 'rgba(91, 79, 240, 0.06)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 3,
            background: COLORS.primaryLight,
            color: COLORS.primary,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 28 }} />
        </Box>
        <Typography
          sx={{
            fontFamily: "'Fira Sans', sans-serif",
            fontWeight: 700,
            fontSize: '1rem',
            color: COLORS.black,
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.gray }}>
          Tipos aceptados: {acceptedTypes}
        </Typography>

        <input
          id={inputId}
          type="file"
          multiple={multiple}
          accept={acceptedTypes}
          onChange={handleFileInput}
          style={{ display: 'none' }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Lista de archivos seleccionados */}
      {files.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography
            sx={{
              fontFamily: "'Fira Sans', sans-serif",
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: COLORS.gray,
              mb: 1.5,
            }}
          >
            Archivos seleccionados ({files.length})
          </Typography>

          <Stack spacing={1}>
            {files.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.25,
                  background: '#FFFFFF',
                  border: '1px solid rgba(91, 79, 240, 0.10)',
                  borderRadius: 2.5,
                  transition: 'border-color 180ms ease',
                  '&:hover': {
                    borderColor: 'rgba(91, 79, 240, 0.25)',
                  },
                }}
              >
                <PdfIcon sx={{ color: COLORS.error, fontSize: 22 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: "'Fira Sans', sans-serif",
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: COLORS.black,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {file.name}
                  </Typography>
                </Box>
                <Badge variant="purple">{formatFileSize(file.size)}</Badge>
                <IconButton
                  size="small"
                  aria-label="Eliminar archivo"
                  onClick={() => handleRemoveFile(index)}
                  sx={{
                    color: COLORS.gray,
                    '&:hover': {
                      color: COLORS.error,
                      background: 'rgba(239, 68, 68, 0.08)',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
